/**
 * The project map's opening fit (the site, its geometry and the visible design layers) is a goTo sized to the view as it
 * measures at that moment. Sri saw the header map stay at world zoom with tiles in a ~450 px band of a full-width
 * header (intermittent, in Chrome; not reproduced headless). Hardening against a stale first measure, not a root-cause
 * fix: if the view's size changes after the fit, or the fit's goTo is interrupted or rejected, redo that fit at most
 * once, and never after the user has moved the map.
 */
export interface OpeningFitSize {
  width: number;
  height: number;
}

/** What prompts a second look at the opening fit */
export type OpeningFitTrigger = { kind: 'resize'; width: number; height: number } | { kind: 'goto-failed' };

export function shouldRedoOpeningFit(
  fit: OpeningFitSize | null | undefined, trigger: OpeningFitTrigger, userMoved: boolean, alreadyRedone: boolean,
): boolean {
  if (alreadyRedone || userMoved || !fit) return false;
  if (trigger.kind === 'goto-failed') return true;
  return Math.round(trigger.width) !== Math.round(fit.width) || Math.round(trigger.height) !== Math.round(fit.height);
}

/** The view has been laid out: a fit computed before that (0 wide or high) has a garbage target */
export function hasViewSize(view: { width?: number; height?: number } | null | undefined): boolean {
  return !!view && view.width > 0 && view.height > 0;
}

/**
 * A fit target worth going to and recording: finite (a 0-wide view's shift makes it NaN), not inverted, with a span on at
 * least one axis. A single site's fit is a box with height but no width, and goTo fits its height.
 */
export function isUsableFitTarget(extent: { xmin: number; ymin: number; xmax: number; ymax: number } | null | undefined): boolean {
  return !!extent && [extent.xmin, extent.ymin, extent.xmax, extent.ymax].every(Number.isFinite)
    && extent.xmax >= extent.xmin && extent.ymax >= extent.ymin
    && (extent.xmax > extent.xmin || extent.ymax > extent.ymin);
}

/**
 * What a trigger does to the opening fit: redo it now, or once the opening goTo in flight has settled (a redo would
 * interrupt it, and an interrupted fit can leave the camera part-way, e.g. at regional scale), or nothing.
 */
export function openingFitAction(
  fit: OpeningFitSize | null | undefined, trigger: OpeningFitTrigger, userMoved: boolean, alreadyRedone: boolean, animating: boolean,
): 'redo' | 'after-animation' | 'none' {
  if (!shouldRedoOpeningFit(fit, trigger, userMoved, alreadyRedone)) return 'none';
  return animating ? 'after-animation' : 'redo';
}
